/**
 * Idle Status Badge Component
 *
 * Displays when user is idle with countdown timer
 * Shows in header near user profile menu
 * Part of PresenceManager automatic features
 */

import { forwardRef } from 'react';
import { Badge, Tooltip } from '@/components/ui';
import { usePresenceManager } from '@/hooks/usePresenceManager';

export interface IdleStatusBadgeProps {
  /**
   * Custom tooltip message
   */
  tooltipMessage?: string;
  /**
   * Badge size
   */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /**
   * Badge color
   */
  color?: string;
  /**
   * Show time remaining in badge
   */
  showTimeRemaining?: boolean;
}

/**
 * Badge showing idle state with countdown timer
 * Only visible when user is idle
 *
 * @example
 * ```tsx
 * <IdleStatusBadge />
 * <IdleStatusBadge showTimeRemaining />
 * ```
 */
export const IdleStatusBadge = forwardRef<HTMLDivElement, IdleStatusBadgeProps>(
  (props, ref) => {
    const {
      tooltipMessage,
      size = 'sm',
      color = 'orange',
      showTimeRemaining = false,
    } = props;

    const { isIdle, formattedIdleTime } = usePresenceManager();

    // Don't render if not idle
    if (!isIdle) {
      return null;
    }

    const badgeContent = showTimeRemaining ? `💤 ${formattedIdleTime}` : '💤 Idle';
    const tooltip = tooltipMessage || `You are idle. Time remaining: ${formattedIdleTime}`;

    return (
      <Tooltip label={tooltip}>
        <div ref={ref}>
          <Badge color={color} variant="filled" size={size}>
            {badgeContent}
          </Badge>
        </div>
      </Tooltip>
    );
  }
);

IdleStatusBadge.displayName = 'IdleStatusBadge';

export default IdleStatusBadge;
